import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { portalRoot } from './TourGuide';
import { toast } from 'sonner';
import { Check, X, Wand2 } from 'lucide-react';
import { pickHead, proposal, HEAD } from '../lib/irrigation';
import { areaCoverage } from '../lib/coverage';
import { catItem } from '../lib/catalog';
import { uid } from '../lib/model';
import { flyTo } from '../lib/vis';
import DecIn from './DecIn';

export const openAutoPlace = (gid) => window.dispatchEvent(new CustomEvent('giss-irr-auto', { detail: gid }));
const preview = (h) => window.dispatchEvent(new CustomEvent('giss-irr-preview', { detail: h }));
const TYPES = ['g27', 'g29', 'g03', 'g28', 'g30'];

// head-to-head sprinkler proposal for one area: preview on the map, then take over or discard
export default function AutoPlaceBar({ t, project, updateProject }) {
  const [gid, setGid] = useState(null);
  const [opt, setOpt] = useState(null);
  useEffect(() => {
    const h = (e) => { const g = (project.gardens || []).find((x) => x.id === e.detail); if (!g) return; setGid(g.id); setOpt(pickHead(g)); flyTo(g.pts); };
    window.addEventListener('giss-irr-auto', h); return () => window.removeEventListener('giss-irr-auto', h);
  }, [project.gardens]);
  const g = (project.gardens || []).find((x) => x.id === gid);
  const heads = useMemo(() => (g && opt?.r > 0 ? proposal(g, project.fittings).heads(opt.r).map((h) => ({ ...h, catId: opt.catId, throw: opt.r })) : []), [g, opt, project.fittings]);
  useEffect(() => { preview(heads.length ? heads : null); }, [heads]);
  useEffect(() => () => preview(null), []);
  if (!g || !opt) return null;
  const now = areaCoverage(g, project.fittings, project.dryStrict), after = areaCoverage(g, [...(project.fittings || []), ...heads], project.dryStrict);
  const close = () => { setGid(null); setOpt(null); preview(null); };
  const take = () => {
    const it = catItem(opt.catId) || { id: opt.catId, name: t.izK_spray, type: 'other' }, n0 = (project.fittings || []).length;
    const add = heads.map((h, i) => ({ id: uid('fit'), type: it.type || 'other', state: 'none', name: `${it.name} (${n0 + i + 1})`, note: '', lat: h.lat, lon: h.lon, media: [], memos: [], reportId: project.reports?.[0]?.id, catId: it.id, color: it.color, sym: it.sym, cat: { name: it.name, dim: it.dim, unit: it.unit, price: it.price, install: it.install }, throw: opt.r, arc: h.arc, dir: h.dir, auto: true }));
    updateProject((p) => ({ ...p, fittings: [...(p.fittings || []), ...add] }));
    toast.success(t.apDone.replace('{n}', add.length));
    close();
  };
  return createPortal(
    <div className="draw-bar auto-place-bar" data-testid="auto-place-bar">
      <div className="row"><Wand2 size={16} /><b>{t.apTitle}: {g.name}</b><button className="icon-btn" data-testid="auto-place-close-btn" onClick={close}><X size={16} /></button></div>
      <div className="row">
        <select data-testid="auto-place-type" value={opt.catId} onChange={(e) => setOpt({ catId: e.target.value, r: HEAD[e.target.value].r })}>{TYPES.map((k) => <option key={k} value={k}>{catItem(k)?.name || k}</option>)}</select>
        <label className="pit-num"><span>{t.spThrow}</span><DecIn data-testid="auto-place-radius" value={opt.r} onNum={(n) => n > 0.5 && setOpt({ ...opt, r: n })} /></label>
      </div>
      <p className="sw-res" data-testid="auto-place-result">{t.apCount.replace('{n}', heads.length)} · {t.dryOk}: {now.ok} % → <b>{after.ok} %</b> · {t.dryDry}: {now.dry} % → <b>{after.dry} %</b></p>
      <p className="muted small-hint">{t.apHint}</p>
      <div className="row">
        <button className="btn btn-outline btn-sm" data-testid="auto-place-discard-btn" onClick={close}><X size={14} /> {t.apDiscard}</button>
        <button className="btn btn-green btn-sm" data-testid="auto-place-accept-btn" disabled={!heads.length} onClick={take}><Check size={14} /> {t.apAccept}</button>
      </div>
    </div>,
    portalRoot(),
  );
}
